import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Box, MenuItem, Alert
} from '@mui/material';
import axios from 'axios';
import {
  filterLetters,
  filterPhone,
  lettersKeyDown,
  digitsKeyDown,
  phoneKeyDown,
} from './utils/inputFilters';
import { API_BASE } from './utils/apiBase';

const govDigits = (value) => String(value || '').replace(/\D/g, '');
const formatSssNumber = (value) => {
  const digits = govDigits(value).slice(0, 10);
  if (digits.length <= 2) return digits;
  if (digits.length <= 9) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 9)}-${digits.slice(9)}`;
};
const formatGov12Number = (value) => {
  const digits = govDigits(value).slice(0, 12);
  if (digits.length <= 4) return digits;
  if (digits.length <= 8) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  return `${digits.slice(0, 4)}-${digits.slice(4, 8)}-${digits.slice(8)}`;
};
const govError = (field, value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw || raw === 'n/a' || raw === 'na' || raw === 'none') return '';
  const digits = govDigits(value);
  if (field === 'sss_no') return digits.length === 10 ? '' : 'SSS must be exactly 10 digits';
  return digits.length === 12 ? '' : `${field === 'philhealth_no' ? 'PhilHealth' : 'Pag-IBIG'} must be exactly 12 digits`;
};

export default function ProfileDialog({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [form, setForm] = useState({ firstname: '', lastname: '', phone: '', address: '', birthdate: '', gender: '', civil_status: '', sss_no: '', philhealth_no: '', pagibig_no: '' });

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/me`, { withCredentials: true });
        const u = res.data?.user || {};
        setForm({
          firstname: u.firstname || '',
          lastname: u.lastname || '',
          phone: u.phone || '',
          address: u.address || '',
          birthdate: u.birthdate || '',
          gender: u.gender || '',
          civil_status: u.civil_status || '',
          sss_no: formatSssNumber(u.sss_no || ''),
          philhealth_no: formatGov12Number(u.philhealth_no || ''),
          pagibig_no: formatGov12Number(u.pagibig_no || '')
        });
        // allow editing only for HR users
        const role = (u.role || '').toLowerCase();
        setIsEditable(role === 'hr');
      } catch (e) {
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  const handleChange = (e) => {
    if (!isEditable) return;
    const { name, value } = e.target;
    let nextValue = value;
    if (name === 'firstname' || name === 'lastname') {
      nextValue = filterLetters(value);
    } else if (name === 'phone') {
      nextValue = filterPhone(value);
    } else if (name === 'sss_no') {
      nextValue = formatSssNumber(value);
    } else if (name === 'philhealth_no' || name === 'pagibig_no') {
      nextValue = formatGov12Number(value);
    }
    setForm(f => ({ ...f, [name]: nextValue }));
  };

  const handleSave = async () => {
    if (!isEditable) return;
    setSaving(true);
    setError(null);
    try {
      const idErrors = [
        govError('sss_no', form.sss_no),
        govError('philhealth_no', form.philhealth_no),
        govError('pagibig_no', form.pagibig_no),
      ].filter(Boolean);
      if (idErrors.length > 0) {
        setError(idErrors[0]);
        setSaving(false);
        return;
      }
      await axios.put(`${API_BASE}/api/me`, form, { withCredentials: true });
      try { await axios.get(`${API_BASE}/api/me`, { withCredentials: true }); } catch (e) {}
      onClose && onClose({ updated: true });
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!open} onClose={() => onClose && onClose({ updated: false })} fullWidth maxWidth="sm">
      <DialogTitle>My Profile</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'grid', gap: 2 }}>
          <TextField label="First name" name="firstname" value={form.firstname} onChange={handleChange} onKeyDown={lettersKeyDown} fullWidth InputProps={{ readOnly: !isEditable }} />
          <TextField label="Last name" name="lastname" value={form.lastname} onChange={handleChange} onKeyDown={lettersKeyDown} fullWidth InputProps={{ readOnly: !isEditable }} />
          <TextField label="Phone" name="phone" value={form.phone} onChange={handleChange} onKeyDown={(e) => phoneKeyDown(e, form.phone)} fullWidth InputProps={{ readOnly: !isEditable }} />
          <TextField label="Address" name="address" value={form.address} onChange={handleChange} fullWidth InputProps={{ readOnly: !isEditable }} />
          <TextField label="Birthdate" name="birthdate" value={form.birthdate || ''} onChange={handleChange} type="date" InputLabelProps={{ shrink: true }} InputProps={{ readOnly: !isEditable }} />
          <TextField label="SSS Number" name="sss_no" value={form.sss_no || ''} onChange={handleChange} onKeyDown={digitsKeyDown} fullWidth placeholder="12-3456789-0" error={!!govError('sss_no', form.sss_no)} helperText={govError('sss_no', form.sss_no) || 'Format: 12-3456789-0'} inputProps={{ inputMode: 'numeric', maxLength: 12 }} InputProps={{ readOnly: !isEditable }} />
          <TextField label="PhilHealth Number" name="philhealth_no" value={form.philhealth_no || ''} onChange={handleChange} onKeyDown={digitsKeyDown} fullWidth placeholder="1234-5678-9012" error={!!govError('philhealth_no', form.philhealth_no)} helperText={govError('philhealth_no', form.philhealth_no) || 'Format: 1234-5678-9012'} inputProps={{ inputMode: 'numeric', maxLength: 14 }} InputProps={{ readOnly: !isEditable }} />
          <TextField label="Pag-IBIG Number" name="pagibig_no" value={form.pagibig_no || ''} onChange={handleChange} onKeyDown={digitsKeyDown} fullWidth placeholder="1234-5678-9012" error={!!govError('pagibig_no', form.pagibig_no)} helperText={govError('pagibig_no', form.pagibig_no) || 'Format: 1234-5678-9012'} inputProps={{ inputMode: 'numeric', maxLength: 14 }} InputProps={{ readOnly: !isEditable }} />
          <TextField select label="Gender" name="gender" value={form.gender || ''} onChange={handleChange} disabled={!isEditable}>
            <MenuItem value="">(not specified)</MenuItem>
            <MenuItem value="Male">Male</MenuItem>
            <MenuItem value="Female">Female</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </TextField>
          <TextField select label="Civil status" name="civil_status" value={form.civil_status || ''} disabled>
            <MenuItem value="">(not specified)</MenuItem>
            <MenuItem value="Single">Single</MenuItem>
            <MenuItem value="Married">Married</MenuItem>
            <MenuItem value="Widowed">Widowed</MenuItem>
            <MenuItem value="Separated">Separated</MenuItem>
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        {isEditable ? (
          <>
            <Button onClick={() => onClose && onClose({ updated: false })} disabled={saving}>Cancel</Button>
            <Button variant="contained" onClick={handleSave} disabled={saving || loading}>{saving ? 'Saving...' : 'Save'}</Button>
          </>
        ) : (
          <Button onClick={() => onClose && onClose({ updated: false })}>Close</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
